import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, Component, EventEmitter, inject, Input, Optional, Output } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { SelectOption } from "@models/ui-models/select-options";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SecurityType } from '@models/shares/security-type';
import { DividerComponent } from '@shared/components/commons/divider/divider.component';
import {
  ShareClassTypeLabelComponent
} from '@shared/components/grid/ag-cells/shares/ag-share-class-type/share-class-type-label/share-class-type-label.component';
import { LinkComponent } from '@shared/components/commons/link/link.component';
import { ValidationErrorComponent } from '@shared/controls/validation-error/validation-error.component';
import {
  AddShareTypeModalComponent
} from '../share-security-holdings-dropdown/add-share-type-modal/add-share-type-modal.component';

@Component({
  selector: 'app-share-security-types-dropdown',
  standalone: true,
  imports: [
    DividerComponent,
    LinkComponent,
    NgTemplateOutlet,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    ShareClassTypeLabelComponent,
    ValidationErrorComponent,
  ],
  templateUrl: 'share-security-types-dropdown.component.html',
  styleUrl: '../share-security-holdings-dropdown/share-security-holdings-dropdown.component.scss'
})
export class ShareSecurityTypesDropdownComponent implements ControlValueAccessor {
  private modalService = inject(NgbModal);

  @Input() width = '100%';
  @Input() placeholder = 'Select Share Type';
  @Input() label = 'Share Type';
  @Input() link = '';
  @Input() text = '';
  @Input() helperText = '';
  @Input() noOptionsText = 'There are no available share types.';
  @Input({ transform: booleanAttribute }) isDisabled = false;
  @Input({ transform: booleanAttribute }) allowAddNewShareType = true;
  @Input() useShareTypes = true;
  @Input() readonly = false;
  @Input() shareTypeOptions: SelectOption<string>[] | undefined;

  @Input({ required: true, alias: 'options' }) set optionsSetter(options: SecurityType[]) {
    this.options = options;

    if (this._originValue) {
      this.writeValue(this._originValue);
    }
  }

  @Output() select = new EventEmitter<SecurityType>();
  @Output() addShareType = new EventEmitter<SecurityType>();

  #value: SecurityType | null = null;
  dropdownOpen = false;
  options: SecurityType[] = [];
  _originValue: string | null = null;

  constructor(@Optional() protected ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  get value(): SecurityType | null {
    return this.#value;
  }

  set value(value: SecurityType | null) {
    this.#value = value;
    this.onChange(this.#value?.securityTypeId ?? null);
  }

  get displayValidationError(): boolean {
    return !!(this.ngControl && this.ngControl.touched && this.ngControl.errors);
  }

  selectOption(selectedOption: SecurityType): void {
    this.value = selectedOption;
    this.select.emit(selectedOption);
  }

  onChange: any = (): void => {
  };
  onTouched: any = (): void => {
  };

  writeValue(value: string): void {
    this._originValue = value;
    this.value = this.options.find((SecurityType) => SecurityType.securityTypeId === value) ?? null;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  getDropdownState(dropdownState: boolean): void {
    this.dropdownOpen = dropdownState;
  }

  openCreateShareTypeModal(): void {
    const modalRef = this.modalService.open(
      AddShareTypeModalComponent,
      {
        size: 'sm',
        centered: true,
      }
    );

    modalRef.componentInstance.securityTypes = this.options;
    modalRef.componentInstance.useShareTypes = this.useShareTypes;

    if (this.shareTypeOptions) {
      modalRef.componentInstance.shareTypeOptions = this.shareTypeOptions;
    }

    modalRef.result.then(
      (result: SecurityType) => this.addShareType.emit(result),
      () => false
    );
  }
}

